import { default as defaultMuiTheme } from './default';
import { fade } from '@material-ui/core/styles/colorManipulator';
import { gtWalsheimFamily, robotoFamily, default as typography } from './typography';
import palette from './palette';
const { spacing, transitions } = defaultMuiTheme;
export const MuiInputPadding = `${spacing(2.75) - 1}px ${spacing(2) - 1}px ${spacing(1.25) - 1}px`;
const overrides = {
    MuiAppBar: {
        colorDefault: {
            backgroundColor: palette.background.paper,
            borderBottom: `1px solid ${palette.divider}`,
        },
    },
    MuiBadge: {
        badge: {
            backgroundColor: palette.primary.main,
            color: palette.common.white,
            fontWeight: typography.fontWeightBold,
            height: 24,
            minWidth: 24,
            borderRadius: 12,
        },
        dot: {
            height: 8,
            width: 8,
        },
        anchorOriginTopRightCircle: {
            top: '25%',
            marginRight: 2,
        },
        colorPrimary: {
            color: palette.common.white,
        },
        colorSecondary: {
            backgroundColor: palette.secondary.main,
        },
    },
    MuiBrand: {
        root: {
            height: spacing(5),
        },
        icon: {
            width: spacing(5),
        },
        divider: {
            height: 29,
        },
        text: {
            color: palette.text.primary,
            fontFamily: gtWalsheimFamily,
            fontSize: 30,
            fontWeight: typography.fontWeightMedium,
            // these margins are for pixel adjustments on the text
            marginBottom: -2,
            marginTop: 2,
            paddingLeft: spacing(2),
            lineHeight: 1,
        },
    },
    MuiButton: {
        root: {
            height: spacing(6),
            padding: spacing(1.5, 3),
            '& + &': {
                marginLeft: spacing(1),
            },
            '&:disabled': {
                opacity: 0.3,
            },
        },
        sizeSmall: {
            height: spacing(4),
            padding: spacing(1, 2),
        },
        sizeLarge: {
            height: spacing(8),
            padding: spacing(1.5, 3),
        },
        fullWidth: {
            '& + &': {
                margin: spacing(1, 0, 0, 0),
            },
        },
        outlined: {
            padding: spacing(1.5, 3),
        },
        text: {
            backgroundColor: 'transparent',
            color: palette.primary.main,
            padding: spacing(1.5, 3),
            '&:hover, &:active': {
                color: palette.primary.dark,
                backgroundColor: 'transparent',
            },
            '&:disabled': {
                color: palette.primary.main,
            },
        },
        contained: {
            backgroundColor: palette.background.default,
            borderColor: palette.divider,
            borderStyle: 'solid',
            borderWidth: 1,
            boxShadow: 'none',
            color: palette.primary.main,
            '&:hover, &:active': {
                boxShadow: 'none',
                backgroundColor: palette.background.default,
                color: palette.primary.dark,
            },
            '&:disabled': {
                backgroundColor: palette.background.default,
                color: palette.primary.main,
            },
        },
        containedPrimary: {
            backgroundColor: palette.primary.main,
            borderColor: 'transparent',
            color: palette.common.white,
            '&:hover, &:active': {
                backgroundColor: palette.primary.dark,
                color: palette.common.white,
            },
            '&:disabled': {
                backgroundColor: palette.primary.main,
                color: palette.common.white,
            },
        },
        containedSecondary: {
            backgroundColor: palette.secondary.main,
            borderColor: 'transparent',
            color: palette.common.white,
            '&:hover, &:active': {
                backgroundColor: palette.secondary.dark,
                color: palette.common.white,
            },
            '&:disabled': {
                backgroundColor: palette.secondary.main,
                color: palette.common.white,
            },
        },
    },
    MuiDrawer: {
        paper: {
            boxSizing: 'border-box',
            maxWidth: 304,
        },
    },
    MuiIconButton: {
        root: {
            color: palette.text.secondary,
            padding: spacing(1),
            '&:hover': {
                backgroundColor: 'transparent',
                color: palette.secondary.main,
            },
        },
        colorPrimary: {
            '&:hover': {
                backgroundColor: 'transparent',
                color: palette.primary.dark,
            },
        },
        colorSecondary: {
            '&:hover': {
                backgroundColor: 'transparent',
                color: palette.secondary.dark,
            },
        },
        colorInherit: {
            '&:hover': {
                color: 'inherit',
            },
        },
    },
    MuiSvgIcon: {
        colorAction: {
            color: palette.common.green,
        },
        colorError: {
            color: palette.common.red,
        },
        colorDisabled: {
            color: palette.divider,
        },
    },
    MuiCard: {
        root: {
            border: `1px solid ${palette.divider}`,
            '&.MuiPaper-elevation1': {
                boxShadow: 'none',
            },
            '&+&': {
                marginTop: spacing(1),
            },
            '& &+&': {
                marginTop: spacing(1),
            },
        },
    },
    MuiCardHeader: {
        root: {
            borderBottom: `1px solid ${palette.divider}`,
        },
        action: {
            alignSelf: 'center',
            margin: spacing(-1, 0, -1, 1),
        },
        avatar: {
            margin: spacing(-1, 2, -1, 0),
        },
        content: {
            display: 'flex',
            margin: spacing(-0.5, 0),
        },
    },
    MuiCardContent: {
        root: {
            '&:last-child': {
                paddingBottom: spacing(2),
            },
        },
    },
    MuiDialogActions: {
        root: {
            justifyContent: 'center',
            margin: 0,
            padding: defaultMuiTheme.spacing(1),
        },
    },
    MuiDialogContent: {
        root: {
            padding: defaultMuiTheme.spacing(2),
        },
    },
    MuiDialogTitle: {
        root: {
            margin: 0,
            padding: defaultMuiTheme.spacing(2),
        },
    },
    MuiListItem: {
        root: {
            color: palette.text.primary,
            height: spacing(7),
            paddingBottom: spacing(2),
            paddingTop: spacing(2),
            '&$selected': {
                backgroundColor: palette.background.paper,
                color: palette.secondary.main,
                '& .MuiListItemIcon-root': {
                    color: palette.secondary.main,
                },
            },
        },
        dense: {
            paddingBottom: spacing(1),
            paddingTop: spacing(1),
        },
        button: {
            '&:hover': { backgroundColor: palette.background.default },
            '&$selected:hover': { backgroundColor: palette.background.default },
        },
    },
    MuiListItemText: {
        root: {
            ...typography.body2,
            fontFamily: robotoFamily,
            fontWeight: typography.fontWeightMedium,
            marginTop: 0,
            marginBottom: 0,
        },
    },
    MuiListItemIcon: {
        root: {
            color: palette.text.secondary,
            minWidth: spacing(2),
            marginRight: spacing(2),
        },
    },
    MuiMenu: {
        list: {
            paddingBottom: 0,
            paddingTop: 0,
        },
    },
    MuiMenuItem: {
        root: {
            paddingBottom: spacing(2),
            paddingTop: spacing(2),
        },
    },
    MuiAvatar: {
        root: {
            ...typography.subtitle2,
            height: spacing(4),
            width: spacing(4),
            '& + &': {
                marginLeft: spacing(1),
            },
        },
        colorDefault: {
            backgroundColor: palette.divider,
            color: palette.text.primary,
        },
        img: {
            objectFit: 'scale-down',
        },
    },
    MuiAvatarGroup: {
        avatar: {
            '& + &': {
                // Needed to compensate for the sibling margin we apply to the avatar
                // above.
                marginLeft: spacing(-1),
            },
        },
    },
    MuiChip: {
        root: {
            fontSize: '.75rem',
            margin: '4px',
            fontWeight: typography.fontWeightBold,
            borderRadius: 4,
            height: 24,
            color: palette.text.primary,
            backgroundColor: palette.divider,
            '& + &': {
                marginLeft: spacing(1),
            },
        },
        label: {
            overflow: 'visible',
        },
        colorPrimary: {
            color: palette.common.white,
            backgroundColor: palette.primary.main,
        },
        deletableColorPrimary: {
            '&:hover, &:active': {
                backgroundColor: palette.primary.dark,
            },
        },
        colorSecondary: {
            color: palette.common.white,
            backgroundColor: palette.secondary.main,
        },
        deletableColorSecondary: {
            '&:hover, &:active': {
                backgroundColor: palette.secondary.dark,
            },
        },
        deleteIcon: {
            height: 16,
            width: 16,
            fontSize: 16,
            color: 'inherit',
        },
        deleteIconColorPrimary: {
            color: 'inherit',
        },
        deleteIconColorSecondary: {
            color: 'inherit',
        },
        icon: {
            height: 16,
            width: 16,
            fontSize: 16,
            color: 'inherit',
        },
        avatar: {
            height: '16px !important',
            width: '16px !important',
            marginLeft: 12,
            backgroundColor: palette.background.paper,
            padding: '10px',
            color: palette.text.primary,
            fontSize: 8,
        },
    },
    MuiCheckbox: {
        root: {
            '&:hover': { backgroundColor: 'transparent' },
            '&$disabled': { opacity: 0.3 },
        },
        colorPrimary: {
            '&:hover': { color: palette.primary.main },
            '&$checked': {
                '&:hover': {
                    backgroundColor: 'transparent',
                    color: palette.primary.dark,
                },
            },
            '&$disabled': { color: palette.text.secondary },
            '&$disabled$checked': { color: palette.primary.main },
        },
        colorSecondary: {
            '&:hover': { color: palette.secondary.main },
            '&$checked': {
                '&:hover': {
                    backgroundColor: 'transparent',
                    color: palette.secondary.dark,
                },
            },
            '&$disabled': { color: palette.text.secondary },
            '&$disabled$checked': { color: palette.secondary.main },
        },
        indeterminate: {
            color: palette.secondary.main,
        },
    },
    MuiRadio: {
        root: {
            '&:hover': { backgroundColor: 'transparent' },
            '&$disabled': { opacity: 0.3 },
        },
        colorPrimary: {
            '&:hover': { color: palette.primary.main },
            '&$checked': {
                '&:hover': {
                    backgroundColor: 'transparent',
                    color: palette.primary.dark,
                },
            },
            '&$disabled': { color: palette.text.secondary },
            '&$disabled$checked': { color: palette.primary.main },
        },
        colorSecondary: {
            '&:hover': { color: palette.secondary.main },
            '&$checked': {
                '&:hover': {
                    backgroundColor: 'transparent',
                    color: palette.secondary.dark,
                },
            },
            '&$disabled': { color: palette.text.secondary },
            '&$disabled$checked': { color: palette.secondary.main },
        },
    },
    MuiSlider: {
        rail: {
            color: palette.divider,
            opacity: 1,
        },
        valueLabel: {
            '& *': {
                borderRadius: 4,
                transform: 'rotate(0deg)',
                background: palette.secondary.main,
                color: palette.common.white,
                fontWeight: typography.fontWeightBold,
            },
        },
    },
    MuiPagination: {
        root: {
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'transparent',
            padding: spacing(1),
        },
        dots: {
            display: 'flex',
            flexDirection: 'row',
        },
        dot: {
            backgroundColor: palette.divider,
            borderRadius: '50%',
            width: spacing(1),
            height: spacing(1),
            margin: spacing(0, 1),
        },
        dotActive: {
            backgroundColor: palette.secondary.main,
        },
    },
    MuiProgress: {
        root: {
            padding: spacing(3),
        },
    },
    MuiProgressStep: {
        root: {
            display: 'flex',
            justifyContent: 'center',
            padding: spacing(0, 1),
        },
    },
    MuiProgressLabel: {
        root: {
            justifyContent: 'space-between',
            textAlign: 'center',
        },
        labelContainer: {
            marginBottom: spacing(1),
        },
        label: {
            fontWeight: typography.fontWeightBold,
            color: palette.text.secondary,
            '&$active': { color: palette.secondary.main },
            '&$completed': { color: palette.text.primary },
        },
    },
    MuiProgressStepIcon: {
        root: {
            color: palette.divider,
            height: spacing(1),
            width: spacing(1),
            zIndex: 1,
        },
        active: { color: palette.secondary.main },
        completed: { color: palette.secondary.main },
    },
    MuiStepConnector: {
        alternativeLabel: {
            backgroundColor: palette.divider,
            bottom: spacing(0.5) / 2,
            height: spacing(0.5),
            left: 'calc(-50% + 2px)',
            right: 'calc(50% + 2px)',
            top: 'auto',
        },
        line: {
            backgroundColor: palette.secondary.main,
            border: 0,
            height: spacing(0.5),
            left: 0,
            position: 'absolute',
            right: `100%`,
            transition: transitions.create('right', {
                easing: transitions.easing.easeInOut,
                duration: transitions.duration.short,
            }),
        },
        active: {
            '& $line': { right: 0 },
        },
        completed: {
            '& $line': { right: 0 },
        },
        lineHorizontal: {
            borderTopWidth: 0,
        },
    },
    MuiFormControl: {
        fullWidth: {
            '& + &': {
                marginTop: spacing(1),
            },
        },
        marginDense: {
            marginTop: spacing(0.5),
        },
    },
    MuiInputBase: {
        root: {
            backgroundColor: palette.background.default,
            border: '1px solid transparent',
            borderRadius: 4,
            color: palette.text.primary,
            fontWeight: typography.fontWeightLight,
            padding: 0,
            '&:hover:not($disabled)': {
                backgroundColor: palette.background.default,
                borderColor: palette.divider,
            },
            '&$focused': {
                backgroundColor: palette.background.default,
                borderColor: palette.divider,
            },
            '&$error': { backgroundColor: fade(palette.common.red, 0.1) },
            '&$disabled': {
                backgroundColor: palette.background.default,
            },
        },
        input: {
            caretColor: palette.secondary.main,
            height: spacing(3),
            lineHeight: `${spacing(3)}px`,
            // -1 here is to account for transparent border
            padding: spacing(1) - 1,
            '&::placeholder': {
                color: palette.text.secondary,
                opacity: 1,
            },
        },
    },
    MuiFilledInput: {
        root: {
            backgroundColor: palette.background.default,
            fontSize: 14,
            // the -1s are to account for the borders
            padding: 0,
            '& .adornment-focused': { display: 'none' },
            '&:hover': {
                backgroundColor: palette.background.default,
                '& .MuiSelect-icon': { color: palette.secondary.main },
            },
            '&$focused': {
                backgroundColor: palette.background.default,
                borderColor: palette.divider,
                '& .adornment-unfocused': { display: 'none' },
                '& .adornment-focused': { display: 'initial' },
            },
            '&$disabled': {
                color: palette.text.primary,
                opacity: 0.4,
                backgroundColor: palette.background.default,
            },
        },
        input: {
            height: spacing(3),
            // the -1s are to account for the borders
            padding: MuiInputPadding,
        },
        adornedEnd: {
            paddingRight: spacing(1),
        },
    },
    MuiInputAdornment: {
        positionEnd: {
            marginRight: spacing(1) - 1,
            '& .MuiIconButton-root': { marginRight: spacing(-1) },
        },
        positionStart: {
            color: palette.text.secondary,
            marginLeft: spacing(1) - 1,
        },
    },
    MuiFormLabel: {
        root: {
            ...typography.subtitle2,
            color: palette.text.secondary,
            fontWeight: typography.fontWeightRegular,
            lineHeight: 1,
            '&$disabled': {
                color: palette.text.secondary,
                opacity: 0.4,
            },
            '&$error': { color: palette.common.red },
            '&$focused': { color: palette.text.secondary },
        },
    },
    MuiInputLabel: {
        filled: {
            left: spacing(2),
            transform: `translate(0, ${spacing(3)}px) scale(1)`,
            '&$shrink': {
                transform: `translate(0, ${spacing(1.25)}px) scale(0.857)`,
            },
        },
    },
    MuiFormHelperText: {
        root: {
            ...typography.caption,
            color: palette.text.secondary,
            fontWeight: typography.fontWeightRegular,
            '&$error': {
                color: palette.common.red,
            },
            '&$disabled': {
                color: palette.text.secondary,
            },
        },
        contained: {
            margin: spacing(0.5, 2, 0),
        },
    },
    MuiSelect: {
        icon: { color: palette.text.secondary },
        iconFilled: { right: spacing(2) - 1 },
        select: {
            '&:focus': {
                backgroundColor: palette.background.default,
                borderRadius: 4,
            },
        },
        selectMenu: {
            boxSizing: 'border-box',
            display: 'flex',
            height: spacing(7.75),
            paddingRight: spacing(7.75),
            paddingTop: spacing(3.75) - 1,
            position: 'relative',
        },
    },
    MuiDropdown: {
        divider: {
            height: spacing(4),
            position: 'absolute',
            right: spacing(6),
            top: spacing(2),
        },
    },
    MuiSwitch: {
        root: {
            color: palette.background.paper,
            width: 28,
            height: 16,
            padding: 0,
            display: 'inline-flex',
            overflow: 'hidden',
        },
        colorSecondary: {
            color: palette.background.paper,
            '&$checked': {
                color: palette.common.white,
                '& + $track': {
                    backgroundColor: palette.secondary.main,
                },
            },
        },
        switchBase: {
            padding: 2,
            color: palette.background.default,
            '&:hover': {
                color: palette.background.paper,
            },
            '&$checked': {
                transform: 'translateX(12px)',
                color: palette.common.white,
                '& + $track': {
                    opacity: 1,
                    backgroundColor: palette.secondary.main,
                },
            },
        },
        thumb: {
            width: 12,
            height: 12,
            boxShadow: 'none',
        },
        track: {
            border: 0,
            borderRadius: 16 / 2,
            opacity: 1,
            backgroundColor: palette.text.secondary,
        },
        checked: {},
    },
    MuiExpansionPanel: {
        root: {
            '&$expanded': { margin: 0 },
            borderBottom: `1px solid ${palette.divider}`,
        },
    },
    MuiExpansionPanelSummary: {
        root: {
            padding: 0,
        },
        content: {
            margin: 0,
            '&$expanded': { margin: '24px 0' },
        },
    },
    MuiExpansionPanelDetails: {
        root: {
            backgroundColor: palette.background.default,
            display: 'block',
            padding: 0,
        },
    },
    MuiTable: {
        root: {
            borderCollapse: 'separate',
            borderSpacing: spacing(0, 1.5),
        },
    },
    MuiTableRow: {
        root: {
            backgroundColor: palette.common.white,
        },
        head: {
            padding: spacing(1),
            backgroundColor: 'inherit',
        },
    },
    MuiTableCell: {
        body: {
            ...typography.body2,
            fontWeight: typography.fontWeightLight,
            color: palette.text.primary,
            padding: spacing(2.75, 2),
            // ignore VirtualizedTable cell styles
            '&:not([class*="MuiVirtualizedTable"])&:not([class*="jss"])': {
                borderTop: `1px solid ${palette.divider}`,
                borderBottom: `1px solid ${palette.divider}`,
                '&:first-child': {
                    borderLeft: `1px solid ${palette.divider}`,
                    borderRadius: ` 4px 0 0 4px`,
                },
                '&:last-child': {
                    borderRight: `1px solid ${palette.divider}`,
                    borderRadius: `0 4px 4px 0`,
                },
            },
        },
        head: {
            ...typography.body2,
            fontWeight: typography.fontWeightLight,
            color: palette.text.secondary,
            border: 'none',
            padding: spacing(0.25, 2),
            '&:first-child': {
                border: 'none',
            },
            '&:last-child': {
                border: 'none',
            },
        },
    },
    MuiTextField: {
        // this is to fix a discrepancy in height between text inputs and dropdowns
        root: {
            '& .MuiInputLabel-filled': {
                transform: `translate(0, ${spacing(2.5) + 1}px) scale(1)`,
                '&.MuiInputLabel-shrink': {
                    transform: `translate(0, ${spacing(1.25)}px) scale(0.857)`,
                },
            },
        },
    },
    MuiToolbar: {
        regular: {
            minHeight: spacing(9),
        },
    },
};
export default overrides;
//# sourceMappingURL=overrides.js.map